<template>
  <div class="max-w-3xl" v-if="page">
    <div class="adthrive-header min-h-[280px] overflow-hidden"></div>
    <Ad mb-4 ad-slot="6966721074" />
    <h1 class="font-slab text-2xl text-brand-orange">{{ page.title }}</h1>
    <div space-y-10 mt-4>
      <LocationProblem v-if="locationError" :error="locationError" subject="mountains near you" />
      <LocationLookup v-model="selectedLocation" />
      <div space-y-4>
        <div v-if="highestPeak || nearestPeak" grid grid-cols-2 gap-6>
          <div v-if="highestPeak">
            <h2 text-2xl font-semibold text-stone-500>Highest Peak</h2>
            <Peak :peak="highestPeak" />
          </div>
          <div v-if="nearestPeak">
            <h2 text-2xl font-semibold text-stone-500>Nearest Peak</h2>
            <Peak :peak="nearestPeak" />
          </div>
        </div>
        <Ad ad-slot="1948746426" />
        <h2 text-2xl font-semibold text-stone-500>Nearby Mountains Map</h2>
        <div w-full h-lg bg-stone-50 bg-opacity-50>
          <LazyMapPeaks
            v-if="peaks?.data"
            :geojson="peaks.data"
            :location="coordinates"
            @markerMove="setMapLocation"
          />
        </div>
        <Ad ad-slot="3658187561" />
        <h2 text-2xl font-semibold text-stone-500>Nearby Peaks List</h2>
        <div grid grid-flow-dense sm:grid-cols-2 gap-2 v-if="peaks?.data">
          <template v-for="peak in displayPeaks" :key="peak.id">
            <Peak :peak="peak" />
          </template>
        </div>
        <div flex justify-center p-5 v-if="peaks && !showAll">
          <Button @click="showAll = true">View All</Button>
        </div>
      </div>
      <div space-y-4 v-if="peaks && selectedLocation?.module === 'currentLocation'">
        <div text-stone-5 prose v-if="isLocationSupported">
          <p v-if="!loading && peaks?.error && !isBot()">An error occurred. Please try again later.</p>
          <p v-else-if="!loading && !displayPeaks && !isBot()">
            Sorry, we could not find any mountain peaks near your location.
          </p>
          <p>
            By default, the site will gather the GPS coordinates directly from your device and then uses that
            information to lookup information about where you are.
          </p>
        </div>
      </div>
      <WikipediaInfo v-if="placeName" :slug="placeName" />
      <div class="prose lg:text-lg">
        <h2 text-3xl font-semibold text-stone-500>{{ page?.subtitle || "What is this?" }}</h2>
        <ContentRenderer v-if="page" :value="page" />
      </div>
    </div>
  </div>
</template>
<script setup>
const { selectedLocation, coordinates, placeName, locationError, setMapLocation, onCoordinatesChange, geolocation } =
  useSelectedLocation();
const { isLocationSupported } = geolocation;

const peaks = ref();
const loading = ref(true);
const showAll = ref(false);

const displayPeaks = computed(() => {
  if (!peaks.value?.data?.features?.length) {
    return [];
  }
  if (showAll.value) {
    return peaks.value.data.features;
  }
  return peaks.value.data.features.slice(0, 10);
});
const highestPeak = computed(() => {
  if (!peaks.value?.data?.features?.length) {
    return null;
  }
  return peaks.value.data.features.reduce((a, b) => (a.properties.ele > b.properties.ele ? a : b));
});

const nearestPeak = computed(() => {
  if (!peaks.value?.data?.features?.length) {
    return null;
  }
  return peaks.value.data.features.reduce((a, b) => (a.properties.distance < b.properties.distance ? a : b));
});

async function update() {
  loading.value = true;
  peaks.value = null;
  try {
    peaks.value = await useFetchPeaks(coordinates.value);
  } catch (error) {
    console.error(error);
  }
  loading.value = false;
}

// update content when selected location changes
onCoordinatesChange(update);

// load page content
const page = await useContent("/peaks");

useSeoMeta({
  title: () => {
    if (placeName.value) {
      return `Mountains near ${placeName.value} - What is my elevation?`;
    }
    return `Mountains near me - What is my elevation?`;
  },
  description: page.value.subtitle,
});

defineOgImage({
  title: page.value.title,
  subtitle: page.value.subtitle,
});
</script>
